import { inject } from '@angular/core';
import { CanMatchFn, ResolveFn, Route, UrlSegment } from '@angular/router';
import { AuthService } from '@konnektu/auth';
import { catchError, map, of, switchMap, take } from 'rxjs';
import { CardViewService, DynamicViewConfig } from './card-view.service';

type DynamicViewType = 'card' | 'section';

const pathOf = (segments: UrlSegment[]) =>
  segments.map((segment) => segment.path).join('/');

export const dynamicViewOverrideCanMatch =
  (type: DynamicViewType): CanMatchFn =>
  (_route, segments) => {
    const cardViewService = inject(CardViewService);
    const path = pathOf(segments);
    return inject(AuthService).isLoggedIn$.pipe(
      take(1),
      switchMap((isLoggedIn) =>
        isLoggedIn ? cardViewService.getViews(type) : of([])
      ),
      switchMap((views) =>
        views.some((view) => view.Path === path)
          ? cardViewService.getView(path, type)
          : of(null)
      ),
      map((view) => !!view),
      catchError(() => of(false))
    );
  };

export const resolveDynamicViewOverride =
  (type: DynamicViewType): ResolveFn<DynamicViewConfig | null> =>
  (route) =>
    inject(CardViewService).getView(pathOf(route.url), type);

export const DYNAMIC_OVERRIDE_ROUTES: Route[] = [
  {
    path: '**',
    canMatch: [dynamicViewOverrideCanMatch('card')],
    resolve: { config: resolveDynamicViewOverride('card') },
    loadComponent: () =>
      import('@konnektu/dynamic/card').then((c) => c.DynamicCardComponent)
  },
  {
    path: '**',
    canMatch: [dynamicViewOverrideCanMatch('section')],
    resolve: { config: resolveDynamicViewOverride('section') },
    loadComponent: () =>
      import('@konnektu/dynamic/section').then((c) => c.DynamicSectionComponent)
  }
];
